import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, WHATSAPP_URL, AULA_EXPERIMENTAL_MSG } from "../data/siteData";

const waLink = `${WHATSAPP_URL}?text=${encodeURIComponent(AULA_EXPERIMENTAL_MSG)}`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-night/90 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="#inicio" data-testid="header-logo-link" aria-label="Soccer Trainer — Início">
            <Logo size={40} />
          </a>

          <nav className="hidden items-center gap-4 xl:gap-6 lg:flex" aria-label="Navegação principal">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`header-nav-${l.id}`}
                className="text-[12px] font-semibold uppercase tracking-wide text-white/75 transition-colors duration-200 hover:text-lawn xl:text-[13px]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-aula"
              className="hidden items-center gap-2 rounded-full bg-lawn px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-navy transition-transform duration-200 hover:scale-105 sm:inline-flex"
            >
              <MessageCircle size={16} strokeWidth={2.5} />
              Aula experimental
            </a>
            <button
              data-testid="header-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Abrir menu"
              className="inline-flex rounded-md p-2 text-white transition-colors hover:text-lawn lg:hidden"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-drawer"
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-night/70 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 flex h-full w-[82%] max-w-sm flex-col border-l border-white/10 bg-navy p-6"
            >
              <div className="mb-8 flex items-center justify-between">
                <Logo size={36} />
                <button
                  data-testid="mobile-drawer-close"
                  onClick={() => setOpen(false)}
                  aria-label="Fechar menu"
                  className="rounded-md p-2 text-white transition-colors hover:text-lawn"
                >
                  <X size={26} />
                </button>
              </div>
              <nav className="flex flex-col gap-1" aria-label="Navegação móvel">
                {NAV_LINKS.map((l, i) => (
                  <motion.a
                    key={l.id}
                    href={`#${l.id}`}
                    data-testid={`mobile-nav-${l.id}`}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    className="rounded-lg px-4 py-3 font-display text-2xl tracking-wide text-white/85 transition-colors hover:bg-royal/40 hover:text-lawn"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-aula"
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-lawn px-5 py-3.5 text-sm font-bold uppercase tracking-wide text-navy"
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                Agende uma aula experimental
              </a>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
